'use client';

import { useTenantOptional } from '@/contexts/TenantContext';
import { useEffect, useState } from 'react';
import Script from 'next/script';
import { GoogleAdsConversion, GoogleAnalytics, GoogleTagManager } from './GoogleAnalytics';
import { ThirdPartyTracking } from './ThirdPartyTracking';
import { DataLayerEnrichment } from './DataLayerEnrichment';
import { UserIdentitySync } from './UserIdentitySync';
import { ConsentModeDefaults } from './ConsentModeDefaults';
import { RouteRoyalTracker } from './RouteRoyalTracker';
import { useUtmCapture } from '@/hooks/useUtmCapture';

const COOKIE_CONSENT_KEY = 'cookie-consent';
const COOKIE_PREFERENCES_KEY = 'cookie-preferences';

/**
 * Parse the conversion label field which can be either:
 * - A plain string label (backwards-compatible): "9Jc6CLGlgP4bEIf9nvpC"
 * - A JSON map of action→label: {"submit_lead_form":"9Jc6...","contact_us":"tMCE..."}
 */
function parseConversionLabels(
  raw?: string
): { conversionLabel?: string; conversionLabels?: Record<string, string> } {
  if (!raw) return {};

  // Try parsing as JSON map first
  if (raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null) {
        return { conversionLabels: parsed as Record<string, string> };
      }
    } catch {
      // Not valid JSON, treat as plain label
    }
  }

  // Plain string label → use as the default
  return { conversionLabel: raw };
}

interface AnalyticsProviderProps {
  children: React.ReactNode;
  /** Organisation ID - only used as fallback when not in TenantContext */
  organisationId?: string;
}

/**
 * Check if analytics consent has been given.
 * Supports both simple consent ('accepted') and granular preferences.
 */
function checkAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;

  // Check simple consent first
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (consent === 'accepted') return true;
  if (consent === 'rejected') return false;

  // Check granular preferences
  const prefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (prefs) {
    try {
      const parsed = JSON.parse(prefs);
      return parsed.analytics === true;
    } catch {
      return false;
    }
  }

  return false;
}

/**
 * Check if marketing consent has been given.
 */
function checkMarketingConsent(): boolean {
  if (typeof window === 'undefined') return false;

  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (consent === 'accepted') return true;
  if (consent === 'rejected') return false;

  const prefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (prefs) {
    try {
      const parsed = JSON.parse(prefs);
      return parsed.marketing === true;
    } catch {
      return false;
    }
  }

  return false;
}

/**
 * Analytics Provider Component
 *
 * Central component that initializes and manages all analytics services.
 * Now consumes analytics configuration directly from TenantContext (populated by Route Royal SiteConfig).
 * Handles cookie consent and loads tracking scripts appropriately.
 *
 * Features:
 * - Respects GDPR cookie consent requirements
 * - Supports granular consent (analytics vs marketing cookies)
 * - Dynamically loads scripts when consent is given
 * - Works with the CookieConsent banner component
 */
export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const tenant = useTenantOptional();
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  // Capture UTM params and click IDs from the URL into sessionStorage
  useUtmCapture();

  // Get analytics from tenant context (populated from Route Royal SiteConfig)
  const analytics = tenant?.analytics;

  useEffect(() => {
    // Check for existing cookie consent on mount
    if (typeof window !== 'undefined') {
      setAnalyticsConsent(checkAnalyticsConsent());
      setMarketingConsent(checkMarketingConsent());
    }

    // Listen for consent changes from CookieConsent banner
    const handleConsentChange = (
      _event: CustomEvent<{ accepted: boolean }>
    ) => {
      // Re-check both consent types when consent changes
      setAnalyticsConsent(checkAnalyticsConsent());
      setMarketingConsent(checkMarketingConsent());
    };

    window.addEventListener(
      'cookie-consent-change' as never,
      handleConsentChange as EventListener
    );

    return () => {
      window.removeEventListener(
        'cookie-consent-change' as never,
        handleConsentChange as EventListener
      );
    };
  }, []);

  // Route Royal first-party tracker runs independently of third-party
  // analytics config. It uses a first-party cookie (rr_vid) and sends data
  // to the site's own backend — no cross-site tracking, so it operates
  // under legitimate interest and does not require cookie consent.
  const hasOrganisation = !!tenant?.organisationId;

  if (!analytics?.enabled) {
    return (
      <>
        {children}
        {hasOrganisation && <RouteRoyalTracker />}
      </>
    );
  }

  const keys = analytics.keys;

  const consentRequired = analytics.consent?.required ?? false;
  const shouldLoadAnalytics = !consentRequired || analyticsConsent;
  const shouldLoadMarketing = !consentRequired || marketingConsent;

  return (
    <>
      {children}

      {/* Route Royal first-party tracker — always active when org exists.
          Independent of third-party consent since it only uses first-party
          cookies and the site's own API. */}
      {hasOrganisation && <RouteRoyalTracker />}

      <ConsentModeDefaults
        analyticsGranted={shouldLoadAnalytics}
        marketingGranted={marketingConsent}
      />

      <DataLayerEnrichment />

      {/* User identity sync — sets GA4 user_id for authenticated users */}
      <UserIdentitySync />

      {/* Analytics scripts (require analytics consent) */}
      {shouldLoadAnalytics && (
        <>
          {/* Google Tag Manager (preferred) or Google Analytics */}
          {keys.googleTagManager ? (
            <GoogleTagManager
              containerId={keys.googleTagManager}
              debugMode={analytics.debugMode}
            />
          ) : keys.googleAnalytics ? (
            <GoogleAnalytics
              measurementId={keys.googleAnalytics}
              debugMode={analytics.debugMode}
            />
          ) : null}

          {/* Analytics tracking scripts (non-marketing) */}
          <ThirdPartyTracking
            {...(keys.microsoftClarity && { clarity: keys.microsoftClarity })}
            {...(keys.hotjar && { hotjar: keys.hotjar })}
            {...(keys.yandexMetrica && { yandexMetrica: keys.yandexMetrica })}
          />
        </>
      )}

      {/* Google Ads Conversion tracking — loads regardless of consent so
          Google can verify the tag exists. Consent Mode v2 (set above)
          ensures ad_storage starts as 'denied', preventing third-party
          cookies until the user explicitly grants marketing consent.
          This satisfies both GDPR and Google's tag verification. */}
      {keys.googleAdsConversion && (
        <GoogleAdsConversion
          conversionId={keys.googleAdsConversion}
          {...parseConversionLabels(keys.googleAdsConversionLabel)}
          debugMode={analytics.debugMode}
        />
      )}

      {/* Marketing scripts (require marketing consent) */}
      {shouldLoadMarketing && (
        <>
          <ThirdPartyTracking
            {...(keys.facebookPixel && { facebookPixel: keys.facebookPixel })}
            {...(keys.linkedInPartner && {
              linkedInPartner: keys.linkedInPartner,
            })}
            {...(keys.twitterPixel && { twitterPixel: keys.twitterPixel })}
            {...(keys.pinterestTag && { pinterestTag: keys.pinterestTag })}
            {...(keys.tiktokPixel && { tiktokPixel: keys.tiktokPixel })}
          />

          {/* Store LinkedIn conversion ID on window for fireLinkedInConversion() */}
          {keys.linkedInConversionId && (
            <Script
              id="linkedin-conversion-config"
              strategy="afterInteractive"
              dangerouslySetInnerHTML={{
                __html: `window.__LINKEDIN_CONVERSION_ID = '${keys.linkedInConversionId}';`,
              }}
            />
          )}
        </>
      )}
    </>
  );
}

/**
 * Hook to trigger cookie consent
 */
export function useCookieConsent() {
  const acceptConsent = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('cookie-consent', 'accepted');
      window.dispatchEvent(
        new CustomEvent('cookie-consent-change', { detail: { accepted: true } })
      );
    }
  };

  const rejectConsent = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('cookie-consent', 'rejected');
      window.dispatchEvent(
        new CustomEvent('cookie-consent-change', {
          detail: { accepted: false },
        })
      );
    }
  };

  return { acceptConsent, rejectConsent };
}
